import React from 'react';
import { Descriptions, Table, Tabs, Tag, Empty, Space } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import OrderStatusFlow from '../../../../components/common/OrderStatusFlow';
import {
  ORDER_STATUS_OPTIONS,
  CHANNEL_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  SHORTAGE_REASON_OPTIONS,
} from '../../../../constants/order';

/**
 * 订单详情共享内容组件
 * 同时被订单列表 Drawer 和独立详情页路由复用
 *
 * @param {Object} props
 * @param {Object} props.orderDetail - { order, pickingDetails, substituteRecords }
 */
const OrderDetailContent = ({ orderDetail }) => {
  const { t } = useTranslation();

  if (!orderDetail) return null;

  const order = orderDetail.order || {};
  const orderItems = order.items || [];
  const pickingDetails = orderDetail.pickingDetails || [];
  const substituteRecords = orderDetail.substituteRecords || [];

  // 计算当前订单状态节点索引
  const getCurrentStatusIndex = () => {
    if (order.outboundCompleteTime) return 4;
    if (order.pickingEndTime) return 3;
    if (order.pickingStartTime) return 2;
    if (order.readyToPickTime) return 1;

    switch (order.orderStatus) {
      case 'CREATED':
        return 0;
      case 'IN_PROGRESS':
        return 2;
      case 'COMPLETED':
        return 4;
      default:
        return 0;
    }
  };

  // 构建状态数据映射
  const buildStatusData = () => {
    const data = {};
    const currentIndex = getCurrentStatusIndex();
    const nodes = [
      { key: 'ordersAccepted', time: order.createTime },
      { key: 'readyToPick', time: order.readyToPickTime },
      { key: 'startPicking', time: order.pickingStartTime },
      { key: 'pickingCompleted', time: order.pickingEndTime },
      { key: 'outboundCompleted', time: order.outboundCompleteTime },
    ];
    nodes.forEach((node, index) => {
      const isCompleted = index < currentIndex;
      const hasTime = !!node.time;
      if (isCompleted || hasTime) {
        data[node.key] = { time: node.time || null, completed: isCompleted };
      }
    });
    return data;
  };

  // 解析 deliveryAddress JSON
  const parseDelivery = () => {
    if (!order.deliveryAddress) return null;
    try {
      return typeof order.deliveryAddress === 'string'
        ? JSON.parse(order.deliveryAddress)
        : order.deliveryAddress;
    } catch (e) {
      return { addressLine1: order.deliveryAddress };
    }
  };
  const delivery = parseDelivery();

  // 商品明细列
  const itemColumns = [
    { title: t('order.order.detail.items.lineNo'), dataIndex: 'lineNo', width: 80, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    { title: t('order.order.detail.items.itemCode'), dataIndex: 'itemCode', width: 150, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    { title: t('order.order.detail.items.originalItemCode'), dataIndex: 'originalItemCode', width: 150, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => v || '-' },
    { title: t('order.order.detail.items.itemName'), dataIndex: 'itemName', width: 200, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    { title: t('order.order.detail.items.planQty'), dataIndex: 'planQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    { title: t('order.order.detail.items.actualPickedQty'), dataIndex: 'actualPickedQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => v ?? '-' },
    { title: t('order.order.detail.items.unitPrice'), dataIndex: 'unitPrice', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => `฿${v?.toFixed(2) || '0.00'}` },
    { title: t('order.order.detail.items.lineAmount'), dataIndex: 'lineAmount', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => `฿${v?.toFixed(2) || '0.00'}` },
    {
      title: t('order.order.detail.items.isFreebie'),
      dataIndex: 'isFreebie',
      width: 90,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <Tag color={v ? 'green' : 'default'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    { title: t('order.order.detail.items.promotion'), dataIndex: 'promotionType', width: 120, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => v || '-' },
    { title: t('order.order.detail.items.returnQty'), dataIndex: 'returnQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => v ?? '-' },
    { title: t('order.order.detail.items.remark'), dataIndex: 'remark', width: 150, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => v || '-' },
  ];

  // 拣选明细列
  const pickingColumns = [
    { title: t('order.order.detail.picking.taskNo'), dataIndex: 'taskNo', width: 150, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    { title: t('order.order.detail.picking.itemCode'), dataIndex: 'itemCode', width: 150, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    { title: t('order.order.detail.picking.itemName'), dataIndex: 'itemName', width: 200, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    { title: t('order.order.detail.picking.planQty'), dataIndex: 'planQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    { title: t('order.order.detail.picking.actualPickedQty'), dataIndex: 'actualQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    {
      title: t('order.order.detail.picking.isCancelled'),
      dataIndex: 'isCancelled',
      width: 90,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <Tag color={v ? 'default' : 'green'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    {
      title: t('order.order.detail.picking.isRepicked'),
      dataIndex: 'isRepicked',
      width: 90,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <Tag color={v ? 'blue' : 'default'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    {
      title: t('order.order.detail.picking.isSubstituted'),
      dataIndex: 'isSubstituted',
      width: 90,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <Tag color={v ? 'orange' : 'default'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    {
      title: t('order.order.detail.picking.isConfirmShort'),
      dataIndex: 'isConfirmShort',
      width: 110,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <Tag color={v ? 'red' : 'default'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    {
      title: t('order.order.detail.picking.shortageReason'),
      dataIndex: 'shortageReason',
      width: 120,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (reason) => {
        if (!reason) return '-';
        const option = SHORTAGE_REASON_OPTIONS.find((opt) => opt.value === reason);
        return t(option?.label || reason);
      },
    },
    { title: t('order.order.detail.picking.pickerName'), dataIndex: 'pickerName', width: 120, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    { title: t('order.order.detail.picking.pickTime'), dataIndex: 'pickTime', width: 180, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-' },
  ];

  // 替换明细列
  const substituteColumns = [
    { title: t('order.order.detail.substitute.originalLineNo'), dataIndex: 'originalLineNo', width: 80, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    { title: t('order.order.detail.substitute.originalItemCode'), dataIndex: 'originalItemCode', width: 150, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    { title: t('order.order.detail.substitute.originalItemName'), dataIndex: 'originalItemName', width: 200, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    { title: t('order.order.detail.substitute.originalQty'), dataIndex: 'originalQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    { title: t('order.order.detail.substitute.originalPrice'), dataIndex: 'originalPrice', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => `฿${v?.toFixed(2) || '0.00'}` },
    { title: t('order.order.detail.substitute.substituteItemCode'), dataIndex: 'substituteItemCode', width: 150, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    { title: t('order.order.detail.substitute.substituteItemName'), dataIndex: 'substituteItemName', width: 200, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    { title: t('order.order.detail.substitute.substituteQty'), dataIndex: 'substituteQty', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    { title: t('order.order.detail.substitute.substitutePrice'), dataIndex: 'substitutePrice', width: 100, align: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => `฿${v?.toFixed(2) || '0.00'}` },
    {
      title: t('order.order.detail.substitute.priceDiff'),
      dataIndex: 'priceDiff',
      width: 100,
      align: 'right',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (diff) => (
        <span style={{ color: diff > 0 ? '#ff4d4f' : diff < 0 ? '#52c41a' : '#000' }}>
          ฿{diff?.toFixed(2) || '0.00'}
        </span>
      ),
    },
    { title: t('order.order.detail.substitute.pickerName'), dataIndex: 'pickerName', width: 120, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    { title: t('order.order.detail.substitute.substituteTime'), dataIndex: 'substituteTime', width: 180, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-' },
  ];

  return (
    <>
      {/* 订单状态流程 */}
      <OrderStatusFlow
        current={getCurrentStatusIndex()}
        statusData={buildStatusData()}
        isCancelled={order.orderStatus === 'CANCELLED'}
        cancelTime={order.cancelTime}
      />

      {/* 基本信息 */}
      <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t('order.order.detail.basic.orderNo')}>
          {order.orderNo || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.upstreamOrderNo')}>
          {order.upstreamOrderNo || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.orderType')}>
          {order.orderType || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.channel')}>
          {CHANNEL_OPTIONS.find((opt) => opt.value === order.channel)?.label || order.channel}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.orderStatus')}>
          <Tag color={ORDER_STATUS_OPTIONS.find((opt) => opt.value === order.orderStatus)?.color}>
            {t(ORDER_STATUS_OPTIONS.find((opt) => opt.value === order.orderStatus)?.label || order.orderStatus)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.paymentMethod')}>
          {PAYMENT_METHOD_OPTIONS.find((opt) => opt.value === order.paymentMethod)?.label || order.paymentMethod}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.storeCode')}>
          {order.storeCode || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.skuQty')}>
          {order.skuQty || 0}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.totalMu')}>
          {order.totalMu || 0}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.totalAmount')}>
          ฿{order.totalAmount?.toFixed(2) || '0.00'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.cancelTime')} span={2}>
          {order.cancelTime ? dayjs(order.cancelTime).format('YYYY-MM-DD HH:mm:ss') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.basic.createTime')} span={2}>
          {order.createTime ? dayjs(order.createTime).format('YYYY-MM-DD HH:mm:ss') : '-'}
        </Descriptions.Item>
      </Descriptions>

      {/* Delivery Info */}
      <Descriptions
        bordered
        column={4}
        size="small"
        title={t('order.order.detail.delivery.title')}
        style={{ marginBottom: 16 }}
      >
        <Descriptions.Item label={t('order.order.detail.delivery.recipientName')}>
          {delivery
            ? [delivery.firstName, delivery.lastName].filter(Boolean).join(' ') || order.buyerName || '-'
            : order.buyerName || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.phoneNumber')}>
          {delivery?.mobileNo || order.buyerPhone || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.note')}>
          {delivery?.deliveryNote || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.address')}>
          {[delivery?.addressLine1, delivery?.addressLine2, delivery?.addressLine3].filter(Boolean).join(', ') || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.subDistrict')}>
          {delivery?.subDistrict || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.district')}>
          {delivery?.city || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.province')}>
          {delivery?.state || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.country')}>
          {delivery?.country || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.postcode')}>
          {delivery?.postalCode || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('order.order.detail.delivery.latLng')} span={3}>
          {delivery?.latitude && delivery?.longitude ? (
            <a
              href={`https://www.google.com/maps?q=${delivery.latitude},${delivery.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#13c2c2' }}
            >
              <EnvironmentOutlined style={{ marginRight: 4 }} />
              {delivery.latitude}, {delivery.longitude}
            </a>
          ) : '-'}
        </Descriptions.Item>
      </Descriptions>

      {/* 三个 Tab */}
      <Tabs
        defaultActiveKey="1"
        type="card"
        items={[
          {
            key: '1',
            label: t('order.order.detail.tabs.orderDetails'),
            children: (
              <Table
                dataSource={orderItems}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: 1200 }}
                locale={{ emptyText: <Empty description={t('common.message.noData')} /> }}
                columns={itemColumns}
              />
            ),
          },
          {
            key: '2',
            label: t('order.order.detail.tabs.pickingDetails'),
            children: (
              <div>
                <Table
                  dataSource={pickingDetails}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  scroll={{ x: 1400 }}
                  locale={{ emptyText: <Empty description={t('order.order.detail.picking.emptyText')} /> }}
                  columns={pickingColumns}
                />
                {pickingDetails.length > 0 && (
                  <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                    <Space>
                      <span>
                        {t('order.order.detail.picking.summaryPicked')}:{' '}
                        {pickingDetails.filter((item) => !item.isCancelled && item.actualQty > 0).length}
                      </span>
                      <span>|</span>
                      <span>
                        {t('order.order.detail.picking.summaryTotal')}: {pickingDetails.length}
                      </span>
                      <span>|</span>
                      <span>
                        {t('order.order.detail.picking.summaryRate')}:{' '}
                        {(
                          (pickingDetails.filter((item) => !item.isCancelled && item.actualQty > 0).length /
                            pickingDetails.length) *
                          100
                        ).toFixed(1)}
                        %
                      </span>
                    </Space>
                  </div>
                )}
              </div>
            ),
          },
          {
            key: '3',
            label: t('order.order.detail.tabs.substituteDetail'),
            children: (
              <Table
                dataSource={substituteRecords}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: 1600 }}
                locale={{ emptyText: <Empty description={t('common.message.noData')} /> }}
                columns={substituteColumns}
              />
            ),
          },
        ]}
      />
    </>
  );
};

export default OrderDetailContent;
